// MAP SYSTEM save section (profile v3, src/app/save.ts): selected map, time-of-day choice per map, best survival time per
// map, unlocked maps. Pure functions (unit-tested); the select page and main.ts call them.
import type { TodChoice } from '../core/mapdef';
import { isUnlocked } from '../core/mapdef';
import { DEFAULT_MAP, isTodChoice, MAPS, mapById } from '../maps/registry';

export interface MapsSave {
  /** last map picked on the select page */
  selected: string;
  /** last time-of-day choice per map (absent = the map's default) */
  tod: Record<string, TodChoice>;
  /** best survival time (s) per map */
  best: Record<string, number>;
  /** maps opened by progress (kept even if a rule changes later: an unlock is never taken back) */
  unlocked: string[];
}

export function emptyMaps(): MapsSave { return { selected: DEFAULT_MAP, tod: {}, best: {}, unlocked: [DEFAULT_MAP] }; }

/** v2 → v3: older saves have no map section. Every run before the map system was on map #1, so its best time seeds
 *  Canyon Meadow's record (a player who already survived long enough opens map #2 straight away). */
export function migrateMaps(raw: unknown, legacyBest = 0): MapsSave {
  const m = emptyMaps();
  const r = raw && typeof raw === 'object' ? (raw as Partial<MapsSave>) : null;
  const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? x : 0);
  if (r?.best && typeof r.best === 'object') for (const [k, v] of Object.entries(r.best)) if (num(v) > 0) m.best[k] = num(v); // unknown ids kept (a map may come back)
  if (!r && legacyBest > 0) m.best[DEFAULT_MAP] = num(legacyBest);
  if (r?.tod && typeof r.tod === 'object') for (const [k, v] of Object.entries(r.tod)) if (isTodChoice(v)) m.tod[k] = v;
  if (Array.isArray(r?.unlocked)) for (const id of r!.unlocked) if (typeof id === 'string' && !m.unlocked.includes(id)) m.unlocked.push(id);
  refreshUnlocks(m);
  if (typeof r?.selected === 'string' && MAPS.some((x) => x.id === r.selected)) m.selected = r.selected;
  return m;
}

/** add every map whose rule is met now; returns the ids that were newly added */
export function refreshUnlocks(m: MapsSave): string[] {
  const added: string[] = [];
  for (const def of MAPS) if (!m.unlocked.includes(def.id) && isUnlocked(def, m)) { m.unlocked.push(def.id); added.push(def.id); }
  return added;
}

/** opened by progress (wip maps can be opened but are only playable once ready, see mapPlayable) */
export const mapOpen = (m: MapsSave, id: string): boolean => m.unlocked.includes(id) || isUnlocked(mapById(id), m);
/** can be picked: opened and finished ('ready'); `all` = the ?mapsall dev override (nothing is saved) */
export const mapPlayable = (m: MapsSave, id: string, all = false): boolean => all || (mapOpen(m, id) && mapById(id).status === 'ready' && mapById(id).id === id);

/** A run ended (death, victory, or quitting a won endless run): keep the best time, open what it unlocks. */
export function recordMapRun(m: MapsSave, mapId: string, time: number): { newBest: boolean; unlocked: string[] } {
  const prev = m.best[mapId] ?? 0, newBest = time > prev;
  if (newBest) m.best[mapId] = Math.floor(time);
  return { newBest, unlocked: refreshUnlocks(m) };
}

/** the time-of-day choice to show for a map: the remembered one, else the map's default */
export const todFor = (m: MapsSave, id: string): TodChoice => m.tod[id] ?? mapById(id).time.default;
